import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { AppShell, SidebarNav, type NavEntry } from '@insurensa/ui';
import { LanguageSwitcher, useFormat, useT } from '@insurensa/i18n';
import { useAuth } from '../lib/auth.js';
import { api } from '../lib/api.js';

interface DashboardCounts {
  partners: { pending: number };
  moderation: { reviewsPending: number };
}

/**
 * Admin console shell.
 *
 * The fixed header and sidebar operators already know, composed from the shared
 * `AppShell` and `SidebarNav` components.
 *
 * The one behavioural difference: navigation is filtered by role. The legacy
 * console rendered the same menu for everyone and relied on nobody typing the
 * URLs of pages they should not reach.
 */
export function Layout(): JSX.Element {
  const { admin, signOut, can } = useAuth();
  const t = useT();
  const fmt = useFormat();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarClosed, setSidebarClosed] = useState(false);

  const { data: counts } = useQuery({
    queryKey: ['dashboard-counts'],
    queryFn: () => api.get<DashboardCounts>('/admin/dashboard'),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const entries: NavEntry[] = [
    { href: '/', label: t('admin.nav.dashboard'), icon: 'pe-7s-graph2' },
    {
      href: '/partners',
      label: t('admin.nav.partners'),
      icon: 'pe-7s-culture',
      ...(counts?.partners.pending ? { badge: counts.partners.pending } : {}),
    },
    { href: '/leads', label: t('admin.nav.leads'), icon: 'pe-7s-note2' },
    { href: '/users', label: t('admin.nav.users'), icon: 'pe-7s-users' },
    {
      href: '/reviews',
      label: t('admin.nav.reviews'),
      icon: 'pe-7s-star',
      ...(counts?.moderation.reviewsPending ? { badge: counts.moderation.reviewsPending } : {}),
    },
    { href: '/app-releases', label: t('admin.nav.appReleases'), icon: 'pe-7s-phone' },
    ...(can('SUPER_ADMIN') ? [{ href: '/team', label: t('admin.nav.team'), icon: 'pe-7s-id' }] : []),
    ...(can('SUPER_ADMIN', 'ADMIN')
      ? [{ href: '/audit-log', label: t('admin.nav.auditLog'), icon: 'pe-7s-safe' }]
      : []),
  ];

  const isActive = (href: string): boolean =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  const handleSignOut = async (): Promise<void> => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <AppShell
      sidebarClosed={sidebarClosed}
      onToggleSidebar={() => setSidebarClosed((value) => !value)}
      headerRight={
        <>
          <LanguageSwitcher className="mr-3" />
          <div className="header-btn-lg pr-0">
            <div className="widget-content p-0">
              <div className="widget-content-wrapper">
                <div className="widget-content-left ml-3 header-user-info">
                  <div className="widget-heading">{admin?.name}</div>
                  <div className="widget-subheading">{admin ? fmt.role(admin.role) : ''}</div>
                </div>
                <div className="widget-content-right ml-3">
                  <button
                    type="button"
                    className="btn-pill btn-shadow btn btn-focus"
                    onClick={() => void handleSignOut()}
                  >
                    {t('common.signOut')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      }
      sidebar={
        <SidebarNav
          entries={entries}
          isActive={isActive}
          renderLink={(entry, _className, content) => (
            <NavLink to={entry.href} end={entry.href === '/'}>
              {content}
            </NavLink>
          )}
        />
      }
    >
      <Outlet />
    </AppShell>
  );
}

/** Back link used on the detail pages. */
export function BackLink({ to, children }: { to: string; children: ReactNode }): JSX.Element {
  return (
    <Link to={to} className="btn btn-outline-secondary btn-sm">
      <i className="pe-7s-angle-left mr-1" />
      {children}
    </Link>
  );
}
